import React from "react";
import { useUpdatingRef } from "@hex-insights/core";

export type TruncateDisplayItemsOptions = {
	includeLast?: boolean;
	numAdditional?: number;
};

/**
 * Truncates the given array so that it contains at most `maxItems` items if the number of given items is less than or
 * equal to `maxItems`, and returns one less than `maxItems` if the number of given items is greater than `maxItems`.
 *
 * This is useful for displaying a list of items in a UI where the list should be truncated if it is too long and a
 * "Show more" button should be displayed to show the rest of the items. This function ensures the total number of
 * rendered elements (including the "Show more" button if necessary) is at most `maxItems`.
 *
 * @param items The items to truncate.
 * @param maxItems The maximum number of items to display.
 * @param includeLast Whether to include the last item in the returned array. Defaults to `false`. If set to `true`, no
 * room is left for an additional UI element (such as a "Show more" button) to be displayed.
 */
export function truncateDisplayItems<T>(
	items: T[],
	maxItems: number | null | undefined,
	{ includeLast = false, numAdditional = 0 }: TruncateDisplayItemsOptions = {},
) {
	maxItems = maxItems ?? items.length;
	let displayedItems = items;
	if (!includeLast && (items.length > maxItems || numAdditional > 0)) {
		displayedItems = items.slice(0, maxItems - 1);
	} else {
		displayedItems = items.slice(0, maxItems);
	}
	const anyHidden = items.length > displayedItems.length;
	const numHidden = items.length - displayedItems.length;
	const totalItems = items.length;
	return {
		displayedItems,
		anyHidden,
		numHidden,
		totalItems,
	};
}

export type UseDynamicTruncationOptions = {
	gapSize?: number;
	axis?: "horizontal" | "vertical";
	/**
	 * Required if container or items are conditionally rendered.
	 */
	syncID?: any;
};

export function useDynamicTruncation(
	containerRef: React.MutableRefObject<HTMLElement | null | undefined>,
	// identity does not need to be stable
	getItem: (container: HTMLElement | null) => HTMLElement | null | undefined,
	{ gapSize = 0, axis = "horizontal", syncID }: UseDynamicTruncationOptions = {},
) {
	const getItemRef = useUpdatingRef(getItem);

	const [maxDisplayed, setMaxDisplayed] = React.useState<number | null>(null);
	const updateMaxDisplayed = React.useCallback(() => {
		if (!containerRef.current) {
			return;
		}
		const item = getItemRef.current(containerRef.current);
		if (!item) {
			return;
		}

		const { width: containerWidth, height: containerHeight } = containerRef.current.getBoundingClientRect();
		const containerSize = axis === "horizontal" ? containerWidth : containerHeight;

		const { width: itemWidth, height: itemHeight } = item.getBoundingClientRect();
		const itemSize = axis === "horizontal" ? itemWidth : itemHeight;

		const md = calculateMaxDisplayedItems(containerSize, itemSize, gapSize);
		setMaxDisplayed(md);
	}, [containerRef, getItemRef, gapSize, axis]);

	React.useLayoutEffect(() => {
		if (!containerRef.current) {
			return;
		}

		updateMaxDisplayed();
	}, [containerRef, updateMaxDisplayed, syncID]);

	React.useEffect(() => {
		if (!containerRef.current) {
			return;
		}

		const observer = new ResizeObserver(updateMaxDisplayed);
		observer.observe(containerRef.current);
		return () => observer.disconnect();
	}, [containerRef, updateMaxDisplayed, syncID]);

	return { maxDisplayed };
}

export function calculateMaxDisplayedItems(containerSize: number, itemSize: number, gapSize = 0) {
	return 1 + Math.max(Math.floor((containerSize - itemSize) / (itemSize + gapSize)), 0);
}
